import { ApiError } from '../types';
import { tokenStorage } from '../utils/tokenStorage';

// HTTP client configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const REQUEST_TIMEOUT = 120000; // 120 seconds
export const LONG_RUNNING_TIMEOUT = 900000; // 15 minutes — for Bedrock-powered operations
const COLD_START_RETRY_CODES = [502, 503];
const MAX_COLD_START_RETRIES = 5;
const COLD_START_RETRY_DELAY = 10000; // 10 seconds between retries

// Silent refresh configuration
const REFRESH_MAX_RETRIES = 3;
const REFRESH_BASE_DELAY = 1000; // 1s, 2s, 4s exponential backoff

// Silent refresh state — prevents concurrent refresh attempts
let refreshPromise: Promise<boolean> | null = null;

async function silentRefresh(): Promise<boolean> {
  const refreshToken = tokenStorage.getRefreshToken();

  for (let attempt = 0; attempt < REFRESH_MAX_RETRIES; attempt++) {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Send in-memory token if available; otherwise rely on httpOnly cookie
        body: JSON.stringify(refreshToken ? { refreshToken } : {}),
        credentials: 'include',
      });

      // 401 = invalid/revoked token — don't retry, fail immediately
      if (res.status === 401) return false;

      // 5xx = transient server error — retry with backoff
      if (res.status >= 500) {
        if (attempt < REFRESH_MAX_RETRIES - 1) {
          await new Promise(r => setTimeout(r, REFRESH_BASE_DELAY * Math.pow(2, attempt)));
          continue;
        }
        return false;
      }

      if (!res.ok) return false;

      const data = await res.json();
      const payload = data.data ?? data; // unwrap ApiResponse if present
      if (payload.accessToken) {
        tokenStorage.setToken(payload.accessToken);
        if (payload.refreshToken) tokenStorage.setRefreshToken(payload.refreshToken);
        if (payload.user) tokenStorage.storeUser(payload.user);
        return true;
      }
      return false;
    } catch {
      // Network error — retry with backoff
      if (attempt < REFRESH_MAX_RETRIES - 1) {
        await new Promise(r => setTimeout(r, REFRESH_BASE_DELAY * Math.pow(2, attempt)));
        continue;
      }
      return false;
    }
  }
  return false;
}

export function attemptSilentRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = silentRefresh().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

// HTTP client class
class HttpClient {
  private baseURL: string;
  private timeout: number;

  constructor(baseURL: string = API_BASE_URL, timeout: number = REQUEST_TIMEOUT) {
    this.baseURL = baseURL;
    this.timeout = timeout;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount: number = 0,
    isRetryAfterRefresh: boolean = false,
    timeout?: number
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const token = tokenStorage.getToken();

    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    // Add authorization header if token exists
    if (token && !tokenStorage.isTokenExpired(token)) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout ?? this.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
        credentials: 'include',
      });

      clearTimeout(timeoutId);

      // Handle authentication errors — attempt silent refresh before logging out
      if (response.status === 401) {
        // Pre-auth endpoints: let 401 fall through to normal error handling
        const preAuthEndpoints = ['/auth/login', '/auth/register', '/auth/mfa/verify', '/auth/mfa/send-otp'];
        const isPreAuth = preAuthEndpoints.some(p => endpoint.startsWith(p));
        if (!isPreAuth) {
          const skipRefreshEndpoints = ['/auth/refresh', '/auth/logout'];
          const isAuthMutation = skipRefreshEndpoints.some(p => endpoint.startsWith(p));
          if (!isRetryAfterRefresh && !isAuthMutation) {
            const refreshed = await attemptSilentRefresh();
            if (refreshed) {
              return this.request<T>(endpoint, options, retryCount, true, timeout);
            }
          }
          tokenStorage.clearAll();
          window.dispatchEvent(new CustomEvent('auth:logout'));
          throw new Error('Authentication required');
        }
      }

      // Auto-retry on 502/503 (cold start) — only for idempotent GET requests.
      // Never retry destructive methods (DELETE, POST, PUT, PATCH) to prevent replaying operations.
      const method = (options.method || 'GET').toUpperCase();
      if (method === 'GET' && COLD_START_RETRY_CODES.includes(response.status) && retryCount < MAX_COLD_START_RETRIES) {
        console.warn(`⏳ Service waking up (${response.status}), retrying in ${COLD_START_RETRY_DELAY / 1000}s... (attempt ${retryCount + 1}/${MAX_COLD_START_RETRIES})`);
        await new Promise(resolve => setTimeout(resolve, COLD_START_RETRY_DELAY));
        return this.request<T>(endpoint, options, retryCount + 1, false, timeout);
      }

      // Handle other HTTP errors
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const apiError: ApiError = {
          code: errorData.error || `HTTP_${response.status}`,
          message: errorData.message || response.statusText,
          details: errorData,
          suggestions: errorData.suggestions || [],
          retryable: response.status >= 500,
        };
        throw apiError;
      }

      // Handle empty responses (like DELETE)
      if (response.status === 204) {
        return undefined as T;
      }

      const jsonResponse = await response.json();

      // Unwrap ApiResponse wrapper if present
      if (jsonResponse && typeof jsonResponse === 'object' && 'data' in jsonResponse) {
        return jsonResponse.data as T;
      }

      return jsonResponse;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }

      throw error;
    }
  }

  async get<T>(endpoint: string, params?: Record<string, any>, timeout?: number): Promise<T> {
    let url = endpoint;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return this.request<T>(url, {}, 0, false, timeout);
  }

  async post<T>(endpoint: string, data?: any, timeout?: number): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }, 0, false, timeout);
  }

  async put<T>(endpoint: string, data?: any, timeout?: number): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }, 0, false, timeout);
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  async patch<T>(endpoint: string, data?: any, timeout?: number): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }, 0, false, timeout);
  }

  /**
   * Download a file from the API as a blob and trigger a browser download.
   * Used for server-side export (PDF, DOCX, TXT).
   */
  async downloadFile(endpoint: string, params?: Record<string, any>): Promise<void> {
    let url = `${this.baseURL}${endpoint}`;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const buildHeaders = (): Record<string, string> => {
      const h: Record<string, string> = {};
      const t = tokenStorage.getToken();
      if (t && !tokenStorage.isTokenExpired(t)) {
        h['Authorization'] = `Bearer ${t}`;
      }
      return h;
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      let response = await fetch(url, { headers: buildHeaders(), signal: controller.signal, credentials: 'include' });

      clearTimeout(timeoutId);

      // Auto-retry on 502/503 (cold start)
      let retryCount = 0;
      while (COLD_START_RETRY_CODES.includes(response.status) && retryCount < MAX_COLD_START_RETRIES) {
        console.warn(`Service waking up (${response.status}), retrying in ${COLD_START_RETRY_DELAY / 1000}s... (attempt ${retryCount + 1}/${MAX_COLD_START_RETRIES})`);
        await new Promise(resolve => setTimeout(resolve, COLD_START_RETRY_DELAY));
        response = await fetch(url, { headers: buildHeaders(), credentials: 'include' });
        retryCount++;
      }

      if (response.status === 401) {
        const refreshed = await attemptSilentRefresh();
        if (refreshed) {
          response = await fetch(url, { headers: buildHeaders(), credentials: 'include' });
        }
        if (response.status === 401) {
          tokenStorage.clearAll();
          window.dispatchEvent(new CustomEvent('auth:logout'));
          throw new Error('Authentication required');
        }
      }

      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText);
        throw new Error(`Export failed: ${errorText}`);
      }

      // Extract filename from Content-Disposition header. Handles three forms:
      //   filename="Smith_v_NHS_Bundle.pdf"
      //   filename=Smith_v_NHS_Bundle.pdf
      //   filename*=UTF-8''Smith_v_NHS_Bundle.pdf  (RFC 5987, percent-encoded)
      // Falls back to "export" only when no header is exposed (e.g. CORS misconfigured).
      const disposition = response.headers.get('Content-Disposition');
      let filename = 'export';
      if (disposition) {
        // Prefer RFC 5987 filename* if present — it's the spec-compliant form for non-ASCII.
        const star = disposition.match(/filename\*\s*=\s*(?:UTF-8'[^']*')?([^;\r\n]+)/i);
        if (star && star[1]) {
          try {
            filename = decodeURIComponent(star[1].trim().replace(/['"]/g, ''));
          } catch {
            filename = star[1].trim().replace(/['"]/g, '');
          }
        } else {
          const plain = disposition.match(/(?:^|;)\s*filename\s*=\s*("([^"]*)"|([^;\r\n]+))/i);
          if (plain) {
            filename = (plain[2] ?? plain[3] ?? '').trim();
          }
        }
        if (!filename) filename = 'export';
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Export request timeout');
      }
      throw error;
    }
  }
}

// Create and export HTTP client instance
export const httpClient = new HttpClient();
export { tokenStorage as TokenManager, API_BASE_URL };
export type { ApiError };